import React from 'react';
import { Box, Alert, Button, CircularProgress, Typography, TextField, Link } from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon, Add as AddIcon, Check as CheckIcon } from '@mui/icons-material';
import DeleteButton from './DeleteButton';

interface EditModeControlsProps {
  isEditMode: boolean;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
  onAddNew: () => void;
  selectedRowsRef: React.MutableRefObject<Set<number>>;
  onDeleteClick: () => void;
  deleteDialogRef: React.MutableRefObject<{ open: () => void; close: () => void } | null>;
  refreshNotificationRef: React.MutableRefObject<{ open: () => void; close: () => void } | null>;
  // Outreach integration props (optional for compatibility with other views)
  listName?: string;
  onListNameChange?: (val: string) => void;
  isNameSaved?: boolean;
  onSaveListName?: () => void;
  onSendToOutreach?: () => void;
  onSendToSearch?: () => void;
  selectedAllInQuery?: boolean;
  onToggleSelectAllInQuery?: () => void;
  totalQueryCount?: number;
}

/**
 * Edit Mode Controls Component
 * Displays the edit mode alert, Save/Cancel buttons, Add/Delete buttons, and loading indicator
 */
const EditModeControls: React.FC<EditModeControlsProps> = ({
  isEditMode,
  saving,
  onSave,
  onCancel,
  onAddNew,
  selectedRowsRef,
  onDeleteClick,
  deleteDialogRef,
  refreshNotificationRef,
  listName = '',
  onListNameChange,
  isNameSaved = false,
  onSaveListName,
  onSendToOutreach,
  onSendToSearch,
  selectedAllInQuery = false,
  onToggleSelectAllInQuery,
  totalQueryCount = 0,
}) => {
  if (!isEditMode) {
    return null;
  }

  return (
    <Box sx={{ mb: 2, mt: 1 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <Alert 
          severity="info" 
          sx={{ 
            flex: 1, 
            minWidth: 200,
            backgroundColor: '#fff3cd',
            color: '#856404',
            '& .MuiAlert-icon': {
              color: '#856404'
            }
          }}
        >
          Edit mode is active. Make your changes, save when done and click refresh to see the changes
        </Alert>
        <Button
          variant="contained"
          color="error"
          startIcon={<SaveIcon />}
          onClick={onSave}
          disabled={saving}
        >
          Save
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<CancelIcon />}
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </Button>
      </Box>
      
      {/* Loading indicator - appears immediately when saving */}
      {saving && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          alignItems: 'center', 
          gap: 1,
          mb: 2,
          pr: 2
        }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Saving changes...
          </Typography>
        </Box>
      )}
      
      {/* Add and Delete buttons - below the alert, on the left side */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          color="primary"
          endIcon={<AddIcon />}
          onClick={onAddNew}
          disabled={saving}
        >
          Add
        </Button>
        <DeleteButton
          selectedRowsRef={selectedRowsRef}
          onDelete={onDeleteClick}
          disabled={saving}
        />

        {/* Unified/Connected Outreach Controls Group */}
        {onSendToOutreach && onListNameChange && onSaveListName && (
          <Box sx={{ display: 'flex', alignItems: 'center', ml: { xs: 0, sm: 2 }, mt: { xs: 1, sm: 0 }, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="List name..."
                value={listName}
                onChange={(e) => onListNameChange(e.target.value)}
                disabled={saving}
                sx={{
                  width: 160,
                  '& .MuiOutlinedInput-root': {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    backgroundColor: isNameSaved ? 'rgba(46, 125, 50, 0.04)' : 'inherit',
                  }
                }}
              />
              <Button
                variant="contained"
                color={isNameSaved ? "success" : "primary"}
                onClick={onSaveListName}
                disabled={saving || !listName.trim()}
                title="Save List Name"
                sx={{
                  borderRadius: 0,
                  minWidth: 40,
                  width: 40,
                  height: 40,
                  marginLeft: '-1px',
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' }
                }}
              >
                <CheckIcon sx={{ fontSize: 20 }} />
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={onSendToOutreach}
                disabled={saving || !isNameSaved || (!selectedAllInQuery && selectedRowsRef.current.size === 0) || (selectedAllInQuery && totalQueryCount === 0)}
                sx={{
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  marginLeft: '-1px',
                  height: 40,
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' }
                }}
              >
                Send to Outreach
              </Button>
              {onSendToSearch && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={onSendToSearch}
                  disabled={saving || !isNameSaved || (!selectedAllInQuery && selectedRowsRef.current.size === 0) || (selectedAllInQuery && totalQueryCount === 0)}
                  sx={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    marginLeft: '4px',
                    height: 40,
                    fontWeight: 600,
                    boxShadow: 'none',
                    '&:hover': { boxShadow: 'none' }
                  }}
                >
                  Send to Search
                </Button>
              )}
            </Box>

            {/* Select All in Query Hyperlink */}
            {onToggleSelectAllInQuery && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={(e) => {
                    e.preventDefault();
                    onToggleSelectAllInQuery();
                  }}
                  sx={{
                    color: selectedAllInQuery ? 'success.main' : 'primary.main',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontWeight: 600,
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    '&:hover': {
                      color: selectedAllInQuery ? 'success.dark' : 'primary.dark',
                    }
                  }}
                >
                  select all in query
                </Link>
                {selectedAllInQuery && (
                  <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                    ({totalQueryCount} entries selected)
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EditModeControls;

